import React, { useState, useCallback, useMemo } from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Avatar,
  Stack,
  Rating,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  useMediaQuery,
  Skeleton,
  alpha,
  Fade,
  Badge,
} from "@mui/material";
import {
  Visibility as ViewsIcon,
  Share as ShareIcon,
  Favorite as LikeIcon,
  Restaurant as CookIcon,
  GetApp as DownloadIcon,
  Schedule as TimeIcon,
  People as ServingIcon,
  LocalFireDepartment as TrendingIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ShareDialog from "../Dialogs/ShareDialog";
import LoginDialog from "../Dialogs/LoginDialog";
import { useRecipeActions } from "../../Hooks/useRecipeActions";
import { useAuth } from "../../Context/AuthContext";
import { getRecipeImage } from "../../constants";
import TimeDisplay from "../Common/TimeDisplay";

// Add the formatTime utility function
const formatTime = (minutes) => {
  if (!minutes) return "0m";

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

// Extract RecipeImageOverlay as a memoized component
const RecipeImageOverlay = React.memo(
  ({ recipe, onLike, onShare, onCook, onDownload }) => {
    const theme = useTheme();

    return (
      <Fade in={true}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 40%, rgba(0,0,0,0.4) 100%)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 2,
            opacity: 0,
            transition: "opacity 0.4s ease",
            "&:hover": {
              opacity: 1,
              "& .action-button": {
                transform: "translateY(0)",
                opacity: 1,
              },
            },
          }}
        >
          {/* Top Section */}
          <Box display="flex" justifyContent="space-between" alignItems="start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                src={recipe.profile_pic}
                sx={{
                  width: 36,
                  height: 36,
                  border: `2px solid ${theme.palette.primary.light}`,
                  boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                }}
              />
              <Box>
                <Typography variant="caption" color="white" fontWeight="bold">
                  {recipe.created_by.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="white"
                  sx={{ display: "block", fontSize: "0.7rem", opacity: 0.8 }}
                >
                  Chef
                </Typography>
              </Box>
            </Stack>

            <Tooltip
              title={
                recipe.likedCount
                  ? `Liked by ${recipe.likedCount} users`
                  : "Like"
              }
            >
              <IconButton
                onClick={onLike}
                className="action-button"
                sx={{
                  color: "white",
                  transform: "translateY(-10px)",
                  opacity: 0,
                  transition: "all 0.3s ease 0.1s",
                  "&:hover": {
                    background: alpha(theme.palette.error.main, 0.3),
                  },
                }}
              >
                <LikeIcon color={recipe.isLiked ? "error" : "inherit"} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Bottom Section */}
          <Box display="flex" justifyContent="space-between" alignItems="end">
            <Stack direction="row" spacing={1}>
              <Tooltip title="Cook now">
                <IconButton
                  onClick={onCook}
                  className="action-button"
                  sx={{
                    color: "white",
                    background: alpha(theme.palette.primary.main, 0.3),
                    transform: "translateY(10px)",
                    opacity: 0,
                    transition: "all 0.3s ease 0.2s",
                    "&:hover": {
                      background: alpha(theme.palette.primary.main, 0.5),
                    },
                  }}
                >
                  <CookIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Share recipe">
                <IconButton
                  onClick={onShare}
                  className="action-button"
                  sx={{
                    color: "white",
                    background: alpha(theme.palette.info.main, 0.3),
                    transform: "translateY(10px)",
                    opacity: 0,
                    transition: "all 0.3s ease 0.3s",
                    "&:hover": {
                      background: alpha(theme.palette.info.main, 0.5),
                    },
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack spacing={1} alignItems="flex-end">
              <Box
                display="flex"
                alignItems="center"
                bgcolor={alpha("#000", 0.5)}
                px={1}
                py={0.5}
                borderRadius={1}
              >
                <ViewsIcon
                  fontSize="small"
                  sx={{ color: theme.palette.grey[300] }}
                />
                <Typography
                  variant="caption"
                  ml={0.5}
                  color="white"
                  fontWeight="medium"
                >
                  {recipe.viewsCount.toLocaleString()}
                </Typography>
              </Box>
              <Tooltip title="Download recipe">
                <IconButton
                  onClick={onDownload}
                  className="action-button"
                  sx={{
                    color: "white",
                    background: alpha(theme.palette.success.main, 0.3),
                    transform: "translateY(10px)",
                    opacity: 0,
                    transition: "all 0.3s ease 0.4s",
                    "&:hover": {
                      background: alpha(theme.palette.success.main, 0.5),
                    },
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Fade>
    );
  }
);

// Extract RecipeStats as a memoized component
const RecipeStats = React.memo(({ recipe }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        mt: 2,
        pt: 2,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Box display="flex" alignItems="center">
            <Rating
              value={parseFloat(recipe.averageRating)}
              precision={0.5}
              readOnly
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiRating-iconFilled": {
                  color: theme.palette.warning.main,
                  filter: "drop-shadow(0px 0px 2px rgba(0,0,0,0.2))",
                },
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              ml={1}
              fontWeight="medium"
            >
              ({recipe.ratingCount})
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={2}>
          <Tooltip title="Servings">
            <Box
              display="flex"
              alignItems="center"
              sx={{
                p: 0.5,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.07),
              }}
            >
              <ServingIcon fontSize="small" color="primary" />
              <Typography variant="body2" ml={0.5} fontWeight="medium">
                {recipe?.serving || 4}
              </Typography>
            </Box>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
});

// Main component with optimizations
const HomePageRecipeCard = ({ recipe, navigationPath, refresh }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Memoize handlers
  const handleView = useCallback(
    () =>
      navigate(`/${navigationPath ? navigationPath : "recipe"}/${recipe.id}`),
    [navigate, navigationPath, recipe.id]
  );

  const { handleLike, handleShare } = useRecipeActions(recipe, refresh);

  const totalTimeInMinutes =
    (parseInt(recipe.cook_time) || 0) + (parseInt(recipe.prep_time) || 0);

  // Memoize card content
  const cardContent = useMemo(
    () => (
      <CardContent sx={{ p: 2.5, cursor: "pointer" }} onClick={handleView}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1.5,
            lineHeight: 1.3,
            display: "-webkit-box",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
          }}
        >
          {recipe.recipe_name}
        </Typography>

        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          gap={0.75}
          mb={1.5}
        >
          {recipe.recipe_category
            ?.split(",")
            ?.slice(0, 3)
            .map((tag) => (
              <Chip
                key={tag}
                label={tag.trim()}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.primary.light, 0.15),
                  color: theme.palette.primary.dark,
                  fontWeight: 500,
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.light, 0.25),
                  },
                }}
              />
            ))}
          {recipe.recipe_category?.split(",").length > 3 && (
            <Chip
              label={`+${recipe.recipe_category?.split(",").length - 3}`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.15),
                color: theme.palette.text.secondary,
                fontWeight: 500,
                borderRadius: 1,
              }}
            />
          )}
          <TimeDisplay
            minutes={totalTimeInMinutes}
            variant="compact"
            tooltip={`Cook: ${formatTime(
              recipe.cook_time
            )} • Prep: ${formatTime(recipe.prep_time)}`}
          />
        </Stack>

        <RecipeStats recipe={recipe} />
      </CardContent>
    ),
    [recipe, handleView, totalTimeInMinutes]
  );

  return (
    <Card
      sx={{
        maxWidth: 345,
        borderRadius: 2,
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: theme.shadows[10],
        },
        bgcolor: "background.paper",
        position: "relative",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Box sx={{ position: "relative", height: 200 }}>
        <CardMedia
          component="img"
          height="100%"
          image={getRecipeImage(recipe.recipe_image) || "/placeholder-16x9.png"}
          alt={recipe.recipe_name}
          sx={{
            objectFit: "cover",
            transition: "transform 0.8s ease",
            "&:hover": {
              transform: "scale(1.05)",
            },
          }}
        />

        <RecipeImageOverlay
          recipe={recipe}
          onLike={handleLike}
          onShare={() => setShareOpen(true)}
          onCook={handleView}
          onDownload={() => console.log("Download recipe")}
        />
      </Box>

      {cardContent}

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        recipe={recipe}
      />

      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </Card>
  );
};

export default React.memo(HomePageRecipeCard);
